/**
 * The properties a portfolio deal is made of.
 *
 * A portfolio package lists its assets — "Oak Terrace, Pine Ridge and Elm
 * Gardens" — and intake read them into the record's deal name. Nothing showed
 * them, and `materialize-portfolio`, the only route that creates them as child
 * deals, had no caller on any screen (stress test CMP-07). This panel lists what
 * the package named, lets a person leave out a misread one, and creates the
 * rest; once created, each child is listed where the package placed it.
 *
 * It renders nothing on a deal that is not a portfolio.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Loader2 } from 'lucide-react'

import { dealRollupService, refreshAfterRollup, rollupProblem } from '@/services/dealRollup'
import { portfolioService } from '@/services/portfolio'

export function PortfolioPropertiesPanel({
  loanId,
  canEdit,
}: {
  loanId: string
  canEdit: boolean
}) {
  const queryClient = useQueryClient()
  const portfolio = useQuery({
    queryKey: ['loan-portfolio', loanId],
    queryFn: () => portfolioService.get(loanId),
    staleTime: 30_000,
  })
  const listed = portfolio.data?.listed_properties ?? []
  // What the reviewer left OUT; everything listed is in until they untick it.
  const [excluded, setExcluded] = useState<string[]>([])
  const chosen = listed.filter((name) => !excluded.includes(name))

  const create = useMutation({
    mutationFn: () =>
      portfolioService.materialize(
        portfolio.data?.source_ingestion_id ?? '',
        chosen,
        'Confirmed on the deal dossier from the properties the package lists',
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-portfolio', loanId] })
    },
  })

  // Taking a property out of the portfolio: it stands as a deal of its own again, with the stage it
  // has. Asked twice, because it changes what the portfolio's roll-up counts.
  const [leaving, setLeaving] = useState<string | null>(null)
  const detach = useMutation({
    mutationFn: (childId: string) => dealRollupService.detach(loanId, [childId]),
    onSuccess: async (_done, childId) => {
      setLeaving(null)
      await refreshAfterRollup(queryClient, { parentId: loanId, loanIds: [childId] })
    },
  })

  const data = portfolio.data
  if (!data || !data.is_portfolio) return null
  const children = data.children ?? []
  const canCreate = canEdit && Boolean(data.source_ingestion_id) && listed.length > 0

  return (
    <section
      aria-label="Portfolio properties"
      className="mb-3 rounded-md border border-stone-800 bg-stone-950/60 px-4 py-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400">
          <Building2 size={12} aria-hidden />
          Portfolio properties
        </div>
        {children.length > 0 && (
          <span className="text-[10px] text-stone-500">{data.summary}</span>
        )}
      </div>

      {children.length > 0 && (
        <ul className="mt-2 divide-y divide-stone-900">
          {children.map((child) => (
            <li key={child.id} className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="truncate text-[12px] text-stone-100">{child.property_name}</span>
              <span className="flex shrink-0 items-baseline gap-3 text-[11px] text-stone-500">
                {child.location}
                {canEdit && leaving !== child.id ? (
                  <button
                    type="button"
                    aria-label={`Remove ${child.property_name} from portfolio`}
                    onClick={() => setLeaving(child.id)}
                    className="text-[10px] text-stone-500 underline-offset-2 hover:text-amber-200 hover:underline"
                  >
                    Remove from portfolio
                  </button>
                ) : null}
                {canEdit && leaving === child.id ? (
                  <span className="flex items-baseline gap-2">
                    <button
                      type="button"
                      disabled={detach.isPending}
                      onClick={() => detach.mutate(child.id)}
                      className="rounded border border-amber-300/40 px-1.5 py-0.5 text-[10px] text-amber-200 hover:bg-amber-300/10 disabled:opacity-40"
                    >
                      {detach.isPending ? 'Removing…' : 'Confirm remove'}
                    </button>
                    <button
                      type="button"
                      disabled={detach.isPending}
                      onClick={() => setLeaving(null)}
                      className="text-[10px] text-stone-500 hover:text-stone-200"
                    >
                      Cancel
                    </button>
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {detach.isError ? (
        <p role="alert" className="mt-1.5 text-[11px] text-red-300">
          {rollupProblem(detach.error).message} The property is still in the portfolio.
        </p>
      ) : null}
      {children.length > 0 && canEdit ? (
        <p className="mt-1.5 text-[10px] text-stone-600">
          Removing a property makes it a deal of its own again; it keeps its stage and its models.
        </p>
      ) : null}

      {listed.length > 0 && (
        <div className="mt-2">
          <p className="text-[11px] leading-relaxed text-stone-400">
            The package lists {listed.length === 1 ? 'this property' : 'these properties'}
            {children.length ? ' beyond those already on the deal' : ''}. Nothing is created
            until a person confirms the list.
          </p>
          <ul className="mt-1.5">
            {listed.map((name) => {
              const checked = chosen.includes(name)
              return (
                <li key={name} className="py-0.5">
                  <label className="flex items-center gap-2 text-[12px] text-stone-100">
                    {canCreate && (
                      <input
                        type="checkbox"
                        aria-label={name}
                        checked={checked}
                        onChange={() =>
                          setExcluded((current) =>
                            checked ? [...current, name] : current.filter((n) => n !== name),
                          )
                        }
                      />
                    )}
                    <span>{name}</span>
                  </label>
                </li>
              )
            })}
          </ul>
          {canCreate && (
            <button
              type="button"
              disabled={chosen.length === 0 || create.isPending}
              onClick={() => create.mutate()}
              className="mt-2 inline-flex items-center gap-1.5 rounded border border-amber-300/40 px-2.5 py-1 text-[11px] text-amber-200 hover:bg-amber-300/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {create.isPending && <Loader2 size={11} className="animate-spin" aria-hidden />}
              {chosen.length === 0
                ? 'Tick the properties to create'
                : `Create ${chosen.length} property record${chosen.length === 1 ? '' : 's'}`}
            </button>
          )}
          {create.isError && (
            <p role="alert" className="mt-1.5 text-[11px] text-red-300">
              The property records were not created. Nothing was changed on the deal.
            </p>
          )}
        </div>
      )}

      {children.length === 0 && listed.length === 0 && (
        <p className="mt-2 text-[11px] text-stone-500">
          The package names this deal a portfolio but lists no properties the engine could read.
        </p>
      )}
    </section>
  )
}

export default PortfolioPropertiesPanel
